import { useEffect, useMemo, useState } from "react";
import api from "@/lib/apiClient";
import { PageHeader, Loader } from "@/components/common";
import { formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Armchair, ShoppingBag, Plus, Minus, ChefHat, Search, Loader2, Leaf, Drumstick, Receipt } from "lucide-react";
import { toast } from "sonner";

export default function Orders() {
  const [tables, setTables] = useState(null);
  const [menu, setMenu] = useState([]);
  const [order, setOrder] = useState(null);
  const [q, setQ] = useState("");
  const [settleOpen, setSettleOpen] = useState(false);
  const [payment, setPayment] = useState("cash");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const loadTables = () => api.get("/restaurant/tables").then((r) => setTables(r.data));
  useEffect(() => { loadTables(); api.get("/products").then((r) => setMenu(r.data)); }, []);

  const openOrder = async (oid) => { const r = await api.get(`/restaurant/orders/${oid}`); setOrder(r.data); };

  const selectTable = async (t) => {
    if (t.order) return openOrder(t.order.id);
    try { const r = await api.post("/restaurant/orders", { type: "dine-in", table_id: t.id }); setOrder({ ...r.data, total: 0 }); loadTables(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const newTakeaway = async () => {
    const r = await api.post("/restaurant/orders", { type: "takeaway" }); setOrder({ ...r.data, total: 0 }); loadTables();
  };

  const addItem = async (d) => {
    if (!order) return;
    const r = await api.post(`/restaurant/orders/${order.id}/items`, { items: [{ item_id: d.id, name: d.name, price: d.selling_price, quantity: 1, veg: d.veg }] });
    setOrder(r.data);
  };
  const fire = async () => {
    if (!order) return;
    await api.post(`/restaurant/orders/${order.id}/kot`, { to: "preparing" });
    toast.success("Sent to kitchen 👨‍🍳"); openOrder(order.id);
  };
  const settle = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/restaurant/orders/${order.id}/settle`, { payment_method: payment, tax_percent: 5, phone });
      const wa = r.data.whatsapp || {};
      toast.success(`Bill settled — ${formatINR(r.data.invoice.total)}`);
      if (wa.sent) toast.success("Invoice sent on WhatsApp ✅");
      else if (phone && wa.reason === "not_configured" && r.data.invoice.pdf_url) {
        const msg = encodeURIComponent(`Here is your bill from KHEVORA Kitchen: ${r.data.invoice.pdf_url}`);
        window.open(`https://wa.me/${phone.replace(/[^\d]/g, "")}?text=${msg}`, "_blank");
      }
      setSettleOpen(false); setOrder(null); setPhone(""); loadTables();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const filtered = useMemo(() => menu.filter((m) => m.name.toLowerCase().includes(q.toLowerCase())), [menu, q]);
  if (!tables) return <Loader />;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="orders-page">
      <PageHeader title="Tables & Orders" subtitle="Tap a table to open a running order, or start a takeaway">
        <Button variant="outline" onClick={newTakeaway} data-testid="new-takeaway-btn" className="font-semibold"><ShoppingBag className="h-4 w-4 mr-1.5" /> New Takeaway</Button>
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Tables grid */}
        <div className="lg:col-span-2 grid grid-cols-3 sm:grid-cols-4 gap-3">
          {tables.map((t) => (
            <button key={t.id} data-testid={`table-${t.name}`} onClick={() => selectTable(t)}
              className={`aspect-square rounded-xl border-2 p-3 flex flex-col items-center justify-center gap-1 transition-all hover:scale-[1.03] ${
                t.occupied ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/40"} ${order && (order.table_id === t.id) ? "ring-2 ring-primary" : ""}`}>
              <Armchair className={`h-6 w-6 ${t.occupied ? "text-primary" : "text-muted-foreground"}`} />
              <div className="font-heading font-bold">{t.name}</div>
              <div className="text-[10px] text-muted-foreground">{t.capacity} seats</div>
              {t.order ? <div className="text-[11px] font-semibold text-primary">{formatINR(t.order.total, { compact: true })}</div>
                : <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Free</div>}
            </button>
          ))}
        </div>

        {/* Order panel */}
        <div className="rounded-xl border border-border bg-card p-4 flex flex-col" data-testid="order-panel">
          {!order ? (
            <div className="text-center text-muted-foreground py-16 text-sm">Select a table or start a takeaway to begin an order.</div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-heading font-bold text-lg">{order.table_name ? `Table ${order.table_name}` : "Takeaway"}</div>
                  <div className="text-xs text-muted-foreground">{order.items?.length || 0} items</div>
                </div>
                <Button size="sm" variant="outline" onClick={fire} data-testid="fire-kot-btn" className="font-semibold"><ChefHat className="h-4 w-4 mr-1" /> Send to Kitchen</Button>
              </div>

              <div className="flex-1 max-h-64 overflow-y-auto space-y-1.5 mb-3">
                {(order.items || []).length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No items yet — add from the menu below.</div>}
                {(order.items || []).map((it, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-border/60 pb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      {it.veg ? <Leaf className="h-3 w-3 text-[hsl(var(--success))] shrink-0" /> : <Drumstick className="h-3 w-3 text-destructive shrink-0" />}
                      <span className="truncate">{it.quantity}× {it.name}</span>
                      <span className={`text-[9px] uppercase px-1 rounded ${it.kot_status === "preparing" ? "bg-warning/20 text-[hsl(var(--warning))]" : it.kot_status === "ready" ? "bg-[hsl(var(--success))]/20 text-[hsl(var(--success))]" : "bg-secondary text-muted-foreground"}`}>{it.kot_status}</span>
                    </div>
                    <span className="tabular-nums">{formatINR(it.price * it.quantity)}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between font-heading font-bold text-lg border-t border-border pt-2 mb-3">
                <span>Total</span><span className="tabular-nums" data-testid="order-total">{formatINR(order.total || 0)}</span>
              </div>
              <Button onClick={() => setSettleOpen(true)} disabled={!(order.items || []).length} data-testid="settle-btn" className="font-semibold w-full"><Receipt className="h-4 w-4 mr-1.5" /> Settle Bill</Button>

              <div className="mt-4 border-t border-border pt-3">
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Add dish…" className="pl-9 h-9" data-testid="menu-search" />
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {filtered.slice(0, 40).map((d) => (
                    <button key={d.id} onClick={() => addItem(d)} data-testid={`add-dish-${d.id}`} className="w-full flex items-center justify-between text-sm rounded-md px-2 py-1.5 hover:bg-secondary transition-colors">
                      <span className="flex items-center gap-2 min-w-0">{d.veg ? <Leaf className="h-3 w-3 text-[hsl(var(--success))]" /> : <Drumstick className="h-3 w-3 text-destructive" />}<span className="truncate">{d.name}</span></span>
                      <span className="flex items-center gap-1 text-muted-foreground"><span className="tabular-nums">{formatINR(d.selling_price)}</span><Plus className="h-3.5 w-3.5 text-primary" /></span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Settle Bill · {formatINR(order?.total || 0)}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Payment method</Label>
              <select value={payment} onChange={(e) => setPayment(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="settle-payment">
                <option value="cash">Cash</option><option value="upi">UPI</option><option value="card">Card</option>
              </select>
            </div>
            <div><Label>Customer WhatsApp (optional — send bill)</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="9876543210" className="mt-1" data-testid="settle-phone" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={settle} disabled={busy} data-testid="confirm-settle-btn" className="font-semibold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm & Bill"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
